import { Link } from "@/lib/router";
import { cn } from "@/utils/cn";

interface NavLinkProps {
  href?: string;
  external?: boolean;
  active?: boolean;
  badge?: string;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}

const Badge: React.FC<{ text: string }> = ({ text }) => (
  <span className="ml-1 rounded-xl bg-[#F35C21] px-1 py-0.5 text-xxs font-light text-white">
    {text}
  </span>
);

/** Pure UI navigation link — handles external/internal, active state, optional badge. */
export const NavLinkItem: React.FC<NavLinkProps> = ({
  href,
  external,
  active,
  badge,
  onClick,
  className,
  children,
}) => {
  const classes = cn(
    "text-sm cursor-pointer font-medium",
    active && "text-cornflowerBlue",
    className,
  );

  const content = (
    <>
      {children}
      {badge && <Badge text={badge} />}
    </>
  );

  // External link → <a> with target="_blank"
  if (href && external) {
    return (
      <a
        data-nav-item
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={classes}
      >
        {content}
      </a>
    );
  }

  // Internal link → router <Link>
  if (href && !onClick) {
    return (
      <Link data-nav-item to={href} className={classes}>
        {content}
      </Link>
    );
  }

  // Action-only → <button>
  return (
    <button data-nav-item type="button" className={classes} onClick={onClick}>
      {content}
    </button>
  );
};
